import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { C } from '../tokens';
import Icon from './Icon';
import useBreakpoint from '../useBreakpoint';

/* ═══════════════════════════════════════════════════════════
   Notification data
   ═══════════════════════════════════════════════════════════ */
const NOTIFICATIONS = [
  { id: 1, icon: 'warning',       iconBg: '#fef2f2', iconColor: C.red,     title: 'API Latency Spike',             desc: '450ms increase on US-East-1 nodes.',          time: '2 min ago',    read: false, category: 'System' },
  { id: 2, icon: 'person_add',    iconBg: '#ecfdf5', iconColor: C.green,   title: 'High-Value Lead Captured',      desc: 'USR-8842 completed Enterprise Pricing intent.',time: '28 min ago',   read: false, category: 'Leads' },
  { id: 3, icon: 'sync_problem',  iconBg: C.pL,      iconColor: C.primary, title: 'Salesforce Sync Failed',        desc: 'Webhook returned 401 Unauthorized.',           time: '1 hr ago',     read: false, category: 'Integration' },
  { id: 4, icon: 'chat',          iconBg: '#eef2ff',  iconColor: C.indigo,  title: 'Escalation: Lina Tan',          desc: 'Customer requested human agent transfer.',     time: '2 hrs ago',    read: true,  category: 'Conversation' },
  { id: 5, icon: 'update',        iconBg: '#f0fdfa',  iconColor: C.teal,    title: 'NLP Engine v2.4 Deployed',      desc: 'Scheduled maintenance completed successfully.',time: '5 hrs ago',    read: true,  category: 'System' },
  { id: 6, icon: 'attach_money',  iconBg: '#fef3c7',  iconColor: C.amber,   title: 'Invoice Paid — FinServe',       desc: 'INV-2023-0891 ($96,000) confirmed.',           time: 'Yesterday',    read: true,  category: 'Billing' },
  { id: 7, icon: 'group_add',     iconBg: '#ecfdf5',  iconColor: C.green,   title: 'New Team Member Joined',        desc: 'Maya Chen added as Support Specialist.',       time: 'Yesterday',    read: true,  category: 'Team' },
  { id: 8, icon: 'security',      iconBg: '#fef2f2',  iconColor: C.red,     title: 'Failed Login Attempt',          desc: '3 failed attempts from IP 103.28.xx.xx.',     time: '2 days ago',   read: true,  category: 'Security' },
  { id: 9, icon: 'smart_toy',     iconBg: '#f0fdfa',  iconColor: C.teal,    title: 'AI Training Complete',          desc: 'Knowledge base re-indexed — 2,400 chunks.',   time: '2 days ago',   read: true,  category: 'AI' },
  { id: 10,icon: 'campaign',      iconBg: '#eef2ff',  iconColor: C.indigo,  title: 'Campaign "Q4 Outreach" Live',   desc: 'Email sequence started for 340 contacts.',    time: '3 days ago',   read: true,  category: 'Marketing' },
];

/* ═══════════════════════════════════════════════════════════
   Profile menu items
   ═══════════════════════════════════════════════════════════ */
const PROFILE_MENU = [
  { icon: 'person',     label: 'My Profile',  desc: 'View and edit your profile' },
  { icon: 'settings',   label: 'Settings',    desc: 'Account preferences' },
  { icon: 'shield',     label: 'Security',    desc: 'Password & 2FA' },
  { icon: 'help',       label: 'Help Center', desc: 'Support & documentation' },
];

/* ═══════════════════════════════════════════════════════════
   Reusable dropdown wrapper
   ═══════════════════════════════════════════════════════════ */
function Dropdown({ open, onClose, anchorRef, align = 'right', width = 360, mob, children }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target) &&
          anchorRef.current && !anchorRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onClose, anchorRef]);

  if (!open) return null;

  /* Mobile: full-width fixed overlay below header */
  if (mob) {
    return (
      <>
        <div onClick={onClose} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.18)',
          zIndex: 998,
        }} />
        <div ref={ref} style={{
          position: 'fixed', top: 56, left: 0, right: 0,
          zIndex: 999, background: C.w,
          borderBottom: `1px solid ${C.bdr}`,
          boxShadow: '0 8px 30px rgba(0,0,0,.12)',
          maxHeight: 'calc(100vh - 56px)', overflowY: 'auto',
          animation: 'dropIn .15s ease-out',
        }}>
          <style>{`@keyframes dropIn{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:translateY(0)}}`}</style>
          {children}
        </div>
      </>
    );
  }

  return (
    <div ref={ref} style={{
      position: 'absolute', top: 'calc(100% + 8px)',
      [align]: 0, width, zIndex: 999,
      background: C.w, borderRadius: 14, border: `1px solid ${C.bdr}`,
      boxShadow: '0 12px 40px rgba(0,0,0,.12), 0 2px 8px rgba(0,0,0,.06)',
      overflow: 'hidden',
      animation: 'dropIn .15s ease-out',
    }}>
      <style>{`@keyframes dropIn{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:translateY(0)}}`}</style>
      {children}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Backdrop Modal (for all notifications)
   ═══════════════════════════════════════════════════════════ */
function Backdrop({ open, onClose, children, width = 580, mob }) {
  if (!open) return null;
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(30,24,18,.45)',
      backdropFilter: 'blur(4px)', zIndex: 99999,
      display: 'grid', placeItems: mob ? 'end center' : 'center',
      padding: mob ? 0 : 24,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: C.w,
        borderRadius: mob ? '18px 18px 0 0' : 18,
        width: '100%', maxWidth: mob ? '100%' : width,
        maxHeight: mob ? '92vh' : '85vh',
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 24px 64px rgba(0,0,0,.18)',
      }}>
        {children}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Main Header Component
   ═══════════════════════════════════════════════════════════ */
export default function Header({ collapsed, onToggle }) {
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();
  const mob = isMobile;
  const [notifOpen, setNotifOpen]       = useState(false);
  const [profileOpen, setProfileOpen]   = useState(false);
  const [allNotifsOpen, setAllNotifsOpen] = useState(false);
  const [notifications, setNotifications] = useState(NOTIFICATIONS);

  const notifRef   = useRef(null);
  const profileRef = useRef(null);

  const unreadCount = notifications.filter(n => !n.read).length;

  const markAsRead = (id) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  };

  const markAllRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const clearAll = () => {
    setNotifications([]);
  };

  /* ═══ Notification Item (shared between dropdown & modal) ═══ */
  const NotifItem = ({ n, compact = false }) => (
    <div
      onClick={() => markAsRead(n.id)}
      style={{
        display: 'flex', gap: 12, padding: compact ? '12px 16px' : '14px 20px',
        cursor: 'pointer', transition: 'background .1s',
        background: n.read ? 'transparent' : `${C.primary}04`,
        borderLeft: n.read ? '3px solid transparent' : `3px solid ${C.primary}`,
      }}
      onMouseEnter={e => e.currentTarget.style.background = '#faf8f7'}
      onMouseLeave={e => e.currentTarget.style.background = n.read ? 'transparent' : `${C.primary}04`}
    >
      <div style={{
        width: compact ? 32 : 38, height: compact ? 32 : 38,
        borderRadius: 10, background: n.iconBg,
        display: 'grid', placeItems: 'center', flexShrink: 0,
      }}>
        <Icon name={n.icon} size={compact ? 16 : 18} style={{ color: n.iconColor }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: compact ? 12 : 13, fontWeight: n.read ? 600 : 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.title}</span>
          {!n.read && <div style={{ width: 7, height: 7, borderRadius: '50%', background: C.primary, flexShrink: 0 }} />}
        </div>
        <p style={{ fontSize: 11, color: C.t2, lineHeight: 1.4, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.desc}</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
          <span style={{ fontSize: 10, color: C.t3 }}>{n.time}</span>
          {!compact && (
            <span style={{ fontSize: 9, fontWeight: 700, color: C.t3, background: '#f1eeec', padding: '1px 6px', borderRadius: 4, textTransform: 'uppercase' }}>{n.category}</span>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <header className={`app-header${collapsed ? ' expanded' : ''}`}>
      {/* Toggle button */}
      <button onClick={onToggle} style={{
        border: 'none', background: 'none', cursor: 'pointer',
        display: 'grid', placeItems: 'center', padding: 6,
        borderRadius: 8, color: C.t2, flexShrink: 0,
      }}>
        <Icon name="menu" size={24} />
      </button>

      {/* Search */}
      <div className="header-search" style={{ flex: 1, maxWidth: 460, position: 'relative' }}>
        <Icon name="search" size={20} style={{
          position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
          color: C.t3, pointerEvents: 'none',
        }} />
        <input type="text" placeholder="Search sessions, users, or alerts…" style={{
          width: '100%', padding: '9px 14px 9px 42px', borderRadius: 12,
          border: 'none', background: C.bg, fontSize: 13, outline: 'none', color: C.t1,
        }} />
      </div>

      <div style={{ flex: 1 }} />

      {/* ═══════════════════════════════════════
          #1 Notification Bell + Dropdown
         ═══════════════════════════════════════ */}
      <div ref={notifRef} style={{ position: 'relative' }}>
        <button
          onClick={() => { setNotifOpen(!notifOpen); setProfileOpen(false); }}
          style={{
            position: 'relative', border: 'none', background: notifOpen ? '#f1eeec' : 'none',
            cursor: 'pointer', padding: 6, borderRadius: 10,
            display: 'grid', placeItems: 'center', color: C.t2,
            transition: 'background .15s',
          }}
        >
          <Icon name="notifications" size={22} />
          {unreadCount > 0 && (
            <span style={{
              position: 'absolute', top: 2, right: 2,
              minWidth: 16, height: 16, borderRadius: 99,
              background: C.primary, border: `2px solid ${C.w}`,
              display: 'grid', placeItems: 'center',
              fontSize: 9, fontWeight: 800, color: '#fff',
              padding: '0 3px',
            }}>{unreadCount}</span>
          )}
        </button>

        {/* Notification Dropdown */}
        <Dropdown open={notifOpen} onClose={() => setNotifOpen(false)} anchorRef={notifRef} width={380} mob={mob}>
          {/* Header */}
          <div style={{ padding: '16px 20px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ fontSize: 15, fontWeight: 700 }}>Notifications</h3>
              {unreadCount > 0 && (
                <p style={{ fontSize: 11, color: C.t3, marginTop: 2 }}>{unreadCount} unread</p>
              )}
            </div>
            {unreadCount > 0 && (
              <button onClick={markAllRead} style={{
                border: 'none', background: 'none', fontSize: 11, fontWeight: 700,
                color: C.primary, cursor: 'pointer',
              }}>Mark all read</button>
            )}
          </div>

          <div style={{ borderTop: `1px solid ${C.bdr}` }} />

          {/* Items (show first 5) */}
          <div style={{ maxHeight: 340, overflowY: 'auto' }}>
            {notifications.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center' }}>
                <Icon name="notifications_none" size={32} style={{ color: C.t3, marginBottom: 8 }} />
                <p style={{ fontSize: 13, color: C.t3 }}>All caught up!</p>
              </div>
            ) : notifications.slice(0, 5).map(n => (
              <div key={n.id} style={{ borderBottom: `1px solid ${C.bdr}` }}>
                <NotifItem n={n} compact />
              </div>
            ))}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div style={{ padding: '12px 20px', borderTop: `1px solid ${C.bdr}`, display: 'flex', justifyContent: 'center' }}>
              <button onClick={() => { setNotifOpen(false); setAllNotifsOpen(true); }} style={{
                border: 'none', background: 'none', fontSize: 12, fontWeight: 700,
                color: C.primary, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
              }}>
                View All Notifications <Icon name="arrow_forward" size={14} />
              </button>
            </div>
          )}
        </Dropdown>
      </div>

      {/* Divider */}
      <div className="header-divider" style={{ width: 1, height: 28, background: C.bdr }} />

      {/* ═══════════════════════════════════════
          #2 Profile Dropdown
         ═══════════════════════════════════════ */}
      <div ref={profileRef} style={{ position: 'relative' }}>
        <button
          onClick={() => { setProfileOpen(!profileOpen); setNotifOpen(false); }}
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            border: 'none', background: profileOpen ? '#f5f3f1' : 'none',
            cursor: 'pointer', padding: '4px 6px 4px 10px', borderRadius: 12,
            transition: 'background .15s',
          }}
        >
          <div className="header-profile-text" style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.2, color: C.t1 }}>Alex Rivera</div>
            <div style={{ fontSize: 11, color: C.t3 }}>Ops Lead</div>
          </div>
          <div style={{
            width: 36, height: 36, borderRadius: '50%',
            background: `linear-gradient(135deg, ${C.primary}, ${C.amber})`,
            display: 'grid', placeItems: 'center', color: '#fff',
            fontWeight: 700, fontSize: 13,
            boxShadow: '0 0 0 2.5px rgba(236,91,19,.18)',
          }}>AR</div>
          <Icon name="expand_more" size={18} style={{ color: C.t3, transition: 'transform .2s', transform: profileOpen ? 'rotate(180deg)' : 'none' }} />
        </button>

        {/* Profile Dropdown */}
        <Dropdown open={profileOpen} onClose={() => setProfileOpen(false)} anchorRef={profileRef} width={260} mob={mob}>
          {/* User info header */}
          <div style={{ padding: '20px 20px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
              background: `linear-gradient(135deg, ${C.primary}, ${C.amber})`,
              display: 'grid', placeItems: 'center', color: '#fff',
              fontWeight: 800, fontSize: 15,
              boxShadow: '0 0 0 3px rgba(236,91,19,.15)',
            }}>AR</div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>Alex Rivera</div>
              <div style={{ fontSize: 12, color: C.t2, marginTop: 1 }}>alex.rivera@nieramind.ai</div>
              <div style={{
                fontSize: 9, fontWeight: 700, color: C.green, background: '#ecfdf5',
                padding: '2px 6px', borderRadius: 4, display: 'inline-block',
                marginTop: 4, textTransform: 'uppercase',
              }}>Online</div>
            </div>
          </div>

          <div style={{ height: 1, background: C.bdr, margin: '0 16px' }} />

          {/* Menu items */}
          <div style={{ padding: '8px 8px' }}>
            {PROFILE_MENU.map((item, i) => (
              <button key={i} onClick={() => {
                setProfileOpen(false);
                if (item.label === 'My Profile' || item.label === 'Settings' || item.label === 'Security') {
                  navigate('/user-settings');
                }
              }} style={{
                display: 'flex', alignItems: 'center', gap: 12, width: '100%',
                padding: '10px 12px', borderRadius: 8, border: 'none',
                background: 'none', cursor: 'pointer', textAlign: 'left',
                transition: 'background .1s',
              }}
                onMouseEnter={e => e.currentTarget.style.background = '#f5f3f1'}
                onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                <div style={{
                  width: 32, height: 32, borderRadius: 8, background: '#f5f3f1',
                  display: 'grid', placeItems: 'center', flexShrink: 0,
                }}>
                  <Icon name={item.icon} size={16} style={{ color: C.t2 }} />
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.t1 }}>{item.label}</div>
                  <div style={{ fontSize: 10, color: C.t3 }}>{item.desc}</div>
                </div>
              </button>
            ))}
          </div>

          <div style={{ height: 1, background: C.bdr, margin: '0 16px' }} />

          {/* Log out */}
          <div style={{ padding: 8 }}>
            <button style={{
              display: 'flex', alignItems: 'center', gap: 10, width: '100%',
              padding: '10px 12px', borderRadius: 8, border: 'none',
              background: 'none', cursor: 'pointer', textAlign: 'left',
              transition: 'background .1s',
            }}
              onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}>
              <div style={{
                width: 32, height: 32, borderRadius: 8, background: '#fef2f2',
                display: 'grid', placeItems: 'center', flexShrink: 0,
              }}>
                <Icon name="logout" size={16} style={{ color: C.red }} />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.red }}>Log Out</div>
                <div style={{ fontSize: 10, color: C.t3 }}>End your session</div>
              </div>
            </button>
          </div>
        </Dropdown>
      </div>

      {/* ═══════════════════════════════════════
          All Notifications Modal
         ═══════════════════════════════════════ */}
      <Backdrop open={allNotifsOpen} onClose={() => setAllNotifsOpen(false)} width={600} mob={mob}>
        {/* Modal header */}
        <div style={{
          padding: mob ? '16px 16px 12px' : '24px 28px 16px', borderBottom: `1px solid ${C.bdr}`,
          display: 'flex', flexDirection: mob ? 'column' : 'row',
          justifyContent: 'space-between', alignItems: mob ? 'stretch' : 'flex-start',
          gap: mob ? 12 : 0,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h3 style={{ fontSize: mob ? 16 : 18, fontWeight: 700 }}>All Notifications</h3>
              <p style={{ fontSize: 12, color: C.t3, marginTop: 4 }}>
                {notifications.length} total • {unreadCount} unread
              </p>
            </div>
            <button onClick={() => setAllNotifsOpen(false)} aria-label="Close" style={{ border: 'none', background: 'none', padding: 4, cursor: 'pointer', flexShrink: 0 }}>
              <Icon name="close" size={20} style={{ color: C.t3 }} />
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {unreadCount > 0 && (
              <button onClick={markAllRead} style={{
                padding: '6px 14px', borderRadius: 6, border: `1px solid ${C.bdr}`,
                background: C.w, fontSize: 11, fontWeight: 700, color: C.t2, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 4, flex: mob ? 1 : 'none',
                justifyContent: 'center',
              }}>
                <Icon name="done_all" size={14} /> Mark all read
              </button>
            )}
            <button onClick={clearAll} style={{
              padding: '6px 14px', borderRadius: 6, border: `1px solid ${C.bdr}`,
              background: C.w, fontSize: 11, fontWeight: 700, color: C.red, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 4, flex: mob ? 1 : 'none',
              justifyContent: 'center',
            }}>
              <Icon name="delete_sweep" size={14} /> Clear all
            </button>
          </div>
        </div>

        {/* Notification list */}
        <div style={{ flex: 1, overflowY: 'auto', maxHeight: '60vh' }}>
          {notifications.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center' }}>
              <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#f5f3f1', display: 'grid', placeItems: 'center', margin: '0 auto 14px' }}>
                <Icon name="notifications_none" size={28} style={{ color: C.t3 }} />
              </div>
              <h4 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>No notifications</h4>
              <p style={{ fontSize: 13, color: C.t3 }}>You're all caught up. New alerts will appear here.</p>
            </div>
          ) : notifications.map((n, i) => (
            <div key={n.id} style={{ borderBottom: i < notifications.length - 1 ? `1px solid ${C.bdr}` : 'none' }}>
              <NotifItem n={n} />
            </div>
          ))}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div style={{
            padding: mob ? '12px 16px' : '14px 28px', borderTop: `1px solid ${C.bdr}`,
            display: 'flex', flexDirection: mob ? 'column-reverse' : 'row',
            justifyContent: 'space-between', alignItems: mob ? 'stretch' : 'center',
            gap: mob ? 10 : 0,
          }}>
            {!mob && (
              <span style={{ fontSize: 11, color: C.t3, display: 'flex', alignItems: 'center', gap: 4 }}>
                <Icon name="info" size={14} style={{ color: C.t3 }} />
                Notifications auto-delete after 30 days.
              </span>
            )}
            <button onClick={() => setAllNotifsOpen(false)} style={{
              padding: mob ? '10px 20px' : '8px 20px', borderRadius: 8, border: 'none',
              background: C.primary, color: '#fff', fontSize: mob ? 13 : 12, fontWeight: 700, cursor: 'pointer',
              width: mob ? '100%' : 'auto',
            }}>Done</button>
          </div>
        )}
      </Backdrop>
    </header>
  );
}
